import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDownIcon, DownloadIcon, FileTextIcon, HistoryIcon, ImageIcon, MapPinIcon, PaperclipIcon, PlusIcon, SparklesIcon } from 'lucide-react';
import type { CitizenRequest, RequestAttachment } from '../../data/demandes';
const attachmentIcons: Record<RequestAttachment['type'], typeof ImageIcon> = {
  image: ImageIcon,
  pdf: FileTextIcon,
  form: FileTextIcon
};
export function RequestDetailsPanel({
  request
}: {
  request: CitizenRequest;
}) {
  const [historyOpen, setHistoryOpen] = useState(false);
  return <div className="space-y-5" data-id="element-858" data-mp="2jhuo6zawatu9">
      <section aria-labelledby="description-title" className="rounded-3xl bg-ville-sand p-6 sm:p-7" data-id="element-859" data-mp="2jjnugdhljc1q">
        <h2 id="description-title" className="text-lg font-extrabold tracking-tight text-ville-navy" data-id="element-860" data-mp="2ogxh5ldlp9lv">
          Request description
        </h2>
        <p className="mt-3 text-[16px] leading-relaxed text-slate-700" data-id="element-861" data-mp="u3g4wuinbz65">{request.description}</p>
        <p className="mt-4 flex items-center gap-1.5 text-xs font-semibold text-slate-500" data-id="element-862" data-mp="2k2an5mw1rb0g">
          <SparklesIcon className="h-3.5 w-3.5 text-ville-indigo" aria-hidden="true" data-id="element-863" data-mp="2k45djgq37vbx" />
          Request created by {request.createdBy}
        </p>
      </section>

      <div className="grid gap-5 sm:grid-cols-2" data-id="element-864" data-mp="2baihrqlvqs2q">
        <section aria-labelledby="location-title" className="rounded-3xl border border-slate-200 p-6" data-id="element-865" data-mp="26b5vb6yx7n0o">
          <h2 id="location-title" className="text-sm font-extrabold uppercase tracking-wider text-slate-500" data-id="element-866" data-mp="330wtbg6f7sb8">
            Location
          </h2>
          <p className="mt-3 flex items-start gap-2 text-[15px] font-bold leading-snug text-ville-navy" data-id="element-867" data-mp="2z0314aly3rcj">
            <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" data-id="element-868" data-mp="2tynh8lvi7qvq" />
            {request.address}
          </p>
          <p className="mt-1 pl-6 text-sm text-slate-500" data-id="element-869" data-mp="1il1u1697fzcu">{request.fullAddress}</p>
        </section>

        <section aria-labelledby="dates-title" className="rounded-3xl border border-slate-200 p-6" data-id="element-870" data-mp="1dmi46urhq3f7">
          <h2 id="dates-title" className="text-sm font-extrabold uppercase tracking-wider text-slate-500" data-id="element-871" data-mp="1f3nlkgepwc11">
            Dates
          </h2>
          <dl className="mt-3 space-y-2 text-[15px]" data-id="element-872" data-mp="1k17bidks2d8t">
            <div className="flex items-baseline justify-between gap-4" data-id="element-873" data-mp="162zfq8zh9hz8">
              <dt className="text-slate-500" data-id="element-874" data-mp="164tqiuh1btpl">Created</dt>
              <dd className="font-bold tabular-nums text-ville-navy" data-id="element-875" data-mp="1b0r8t6buza50">{request.createdOn}</dd>
            </div>
            <div className="flex items-baseline justify-between gap-4" data-id="element-876" data-mp="wc4jqcwfj40l">
              <dt className="text-slate-500" data-id="element-877" data-mp="113waanfl59qq">Resolved</dt>
              <dd className={`font-bold tabular-nums ${request.resolvedOn ? 'text-ville-navy' : 'text-slate-300'}`} data-id="element-878" data-mp="115pfdyajvlwm">
                {request.resolvedOn ?? '-- / -- / --'}
              </dd>
            </div>
          </dl>
        </section>
      </div>

      <section aria-labelledby="files-title" className="rounded-3xl border border-slate-200 p-6 sm:p-7" data-id="element-879" data-mp="detull4ihqyv">
        <h2 id="files-title" className="text-sm font-extrabold uppercase tracking-wider text-slate-500" data-id="element-880" data-mp="icdbytwiajv1">
          Attached files
        </h2>

        {request.attachments.length === 0 ? <p className="mt-3 text-[15px] text-slate-500" data-id="element-881" data-mp="3pzh4vd8tcoyd">No attached files.</p> : <ul className="mt-4 space-y-2" data-id="element-882" data-mp="8r3vyfosa54x">
            {request.attachments.map(file => {
          const Icon = attachmentIcons[file.type];
          return <li key={file.name} className="flex items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3" data-id="element-883" data-mp="hp5sahxko9lv">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-ville-mist text-ville-deep" aria-hidden="true" data-id="element-884" data-mp="rfqyorjhmifc">
                    <Icon className="h-5 w-5" data-id="element-885" data-mp="10gfdvpkmaott" />
                  </span>
                  <span className="min-w-0 flex-1" data-id="element-886" data-mp="10em3bz6vvkgj">
                    <span className="block truncate text-[15px] font-bold text-ville-navy" data-id="element-887" data-mp="wg4yjmaxqm2a">{file.name}</span>
                    <span className="block text-xs text-slate-500" data-id="element-888" data-mp="2px16hrshwnd7">{file.size}</span>
                  </span>
                  <button type="button" aria-label={`Download ${file.name}`} className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-889" data-mp="2uvcgc2isixvf">
                    <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-890" data-mp="2byukb8wwsbtd" />
                  </button>
                </li>;
        })}
          </ul>}

        <button type="button" className="mt-4 inline-flex items-center gap-2 rounded-2xl border border-dashed border-slate-300 px-4 py-3 text-sm font-bold text-slate-500 transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-891" data-mp="2kyzola6t2u3b">
          <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-892" data-mp="1gofuozejvith" />
          Add a file
          <PaperclipIcon className="h-4 w-4 opacity-50" aria-hidden="true" data-id="element-893" data-mp="1bxzex8n9bl3y" />
        </button>
      </section>

      <section aria-labelledby="history-title" className="rounded-3xl border border-slate-200 px-6 py-4 sm:px-7" data-id="element-894" data-mp="16zo52xwypalq">
        <h2 id="history-title" className="sr-only" data-id="element-895" data-mp="120bh29uctxhk">
          Request history
        </h2>
        <button type="button" onClick={() => setHistoryOpen(open => !open)} aria-expanded={historyOpen} className="flex w-full items-center justify-between gap-4 py-1 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-896" data-mp="x0gpe42r93vg">
          <span className="inline-flex items-center gap-2 rounded-full bg-ville-mist px-3 py-1.5 text-sm font-bold text-ville-deep" data-id="element-897" data-mp="2ndo1hywlmi48">
            <HistoryIcon className="h-4 w-4" aria-hidden="true" data-id="element-898" data-mp="2ieumu35ksl2h" />
            History
          </span>
          <span className="flex items-center gap-2 text-sm text-slate-500" data-id="element-899" data-mp="31hn1hlu2s69t">
            {request.timeline.length} steps
            <ChevronDownIcon className={`h-5 w-5 transition-transform duration-200 ease-ville ${historyOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-900" data-mp="36bhiuim7k95q" />
          </span>
        </button>

        {historyOpen && <motion.ol initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="mt-4 space-y-4 border-t border-slate-100 pt-4" data-id="element-901" data-mp="3l7ytf7mkpjr8">
            {request.timeline.map((step, index) => <li key={step.label} className="flex gap-4" data-id="element-902" data-mp="3g69ehgw8clxv" data-mpi={index}>
                <div className="flex flex-col items-center" data-id="element-903" data-mp="1zt1fzxdnxo8t">
                  <span className={`h-3 w-3 rounded-full ${index === request.timeline.length - 1 ? 'bg-ville-indigo' : 'bg-slate-300'}`} aria-hidden="true" data-id="element-904" data-mp="1zvnmdtothxpg" />
                  {index < request.timeline.length - 1 && <span className="mt-1 w-px flex-1 bg-slate-200" aria-hidden="true" data-id="element-905" data-mp="269um5jpzs6vu" />}
                </div>
                <div className="pb-1" data-id="element-906" data-mp="21ahu8kk9k9me">
                  <p className="text-sm font-bold text-ville-navy" data-id="element-907" data-mp="218nb9bsjyrky">{step.label}</p>
                  <p className="text-sm text-slate-500" data-id="element-908" data-mp="1dlowcik2zrp5">{step.date}</p>
                </div>
              </li>)}
          </motion.ol>}
      </section>
    </div>;
}